function initPublicBlogSearch() {
	const forms = Array.from(document.querySelectorAll('.public-blog-search-form'));
	let paginatorInstances = [];
	let paginationRequest = null;
	let paginatorModulesPromise = null;
	let paginatorMountVersion = 0;

	const loadingMarkup =
		'<div class="blog-search-results-loading" role="status" aria-live="polite">' +
			'<span class="spinner-border spinner-border-sm" aria-hidden="true"></span>' +
			'<span>Loading search results...</span>' +
		'</div>';

	const setPaginatorsDisabled = (disabled) => {
		paginatorInstances.forEach(({ vm }) => vm.setDisabled(disabled));
	};

	const setSearchControlsDisabled = (disabled) => {
		forms.forEach((form) => {
			const input = form.querySelector('input[name="q"]');
			const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
			if (input != null) input.disabled = disabled;
			if (submitButton != null) submitButton.disabled = disabled;
		});
	};

	const setPaginatorPageIndexes = (pageIndexes) => {
		paginatorInstances.forEach(({ vm }, index) => vm.setPageIndex(pageIndexes[index] ?? 0));
	};

	const unmountPaginators = () => {
		paginatorMountVersion++;
		paginatorInstances.forEach(({ app }) => app.unmount());
		paginatorInstances = [];
	};

	const mountPaginators = async () => {
		const mountVersion = ++paginatorMountVersion;
		const mountElements = Array.from(document.querySelectorAll('.blog-search-paginator'));
		if (!mountElements.length) return;

		paginatorModulesPromise ??= Promise.all([
			import('vue'),
			import('../vue/blog/BlogSearchPaginator.vue')
		]);
		const [{ createApp }, { default: cmpBlogSearchPaginator }] = await paginatorModulesPromise;
		if (mountVersion !== paginatorMountVersion) return;

		paginatorInstances = mountElements
			.map((mountElement) => {
				const searchUrl = mountElement.dataset.searchUrl ?? '/blog-search/';
				const app = createApp(cmpBlogSearchPaginator, {
					initialPageIndex: Number(mountElement.dataset.pageIndex ?? 0),
					pageSize: Number(mountElement.dataset.pageSize ?? 10),
					nrRecords: Number(mountElement.dataset.nrRecords ?? 0),
					onPaginate: (pageIndex) => loadPage(searchUrl, pageIndex)
				});
				return { app, vm: app.mount(mountElement) };
			});
	};

	const showResultsLoading = (currentResults) => {
		const resultsTop = currentResults.getBoundingClientRect().top + window.scrollY - 16;
		window.scrollTo({ top: Math.max(0, resultsTop), behavior: 'auto' });
		setPaginatorsDisabled(true);
		currentResults.setAttribute('aria-busy', 'true');

		const resultsContent = currentResults.querySelector('.blog-search-results-content');
		if (resultsContent != null) {
			resultsContent.innerHTML = loadingMarkup;
		}
		else {
			currentResults.innerHTML = loadingMarkup;
		}
	};

	const loadPage = async (searchUrl, pageIndex, resetPaginators = false) => {
		const currentResults = document.getElementById('blog-search-current-results');
		if (currentResults == null) return;

		paginatorMountVersion++;
		paginationRequest?.abort();
		const requestController = new AbortController();
		paginationRequest = requestController;
		setSearchControlsDisabled(true);

		const currentContent = currentResults.querySelector('.blog-search-results-content');
		const originalContentHtml = currentContent?.innerHTML ?? null;
		const originalPaginatorPageIndexes = paginatorInstances.map(({ vm }) => vm.pageIndex);
		if (resetPaginators) {
			setPaginatorPageIndexes(paginatorInstances.map(() => 0));
		}
		const requestUrl = new URL(searchUrl, window.location.origin);
		requestUrl.searchParams.set('page', pageIndex + 1);
		requestUrl.searchParams.set('results', 'true');
		showResultsLoading(currentResults);

		try {
			const response = await fetch(requestUrl, {
				headers: {
					'X-Requested-With': 'XMLHttpRequest'
				},
				signal: requestController.signal
			});

			if (!response.ok) {
				throw new Error('Blog search request failed with status ' + response.status + '.');
			}

			const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
			const nextResults = responseDocument.getElementById('blog-search-current-results');
			if (nextResults == null) {
				throw new Error('Blog search response did not contain a results section.');
			}

			unmountPaginators();
			currentResults.innerHTML = nextResults.innerHTML;
			currentResults.removeAttribute('aria-busy');
			const resolvedPaginator = nextResults.querySelector('.blog-search-paginator');
			const resolvedPageIndex = Math.max(0, Number(resolvedPaginator?.dataset.pageIndex) || 0);
			const resolvedSearchTerm = requestUrl.searchParams.get('q') ?? '';
			window.sessionStorage.setItem(
				'blog-search-page:' + resolvedSearchTerm,
				resolvedPageIndex.toString()
			);
			await mountPaginators();
		}
		catch (error) {
			if (error.name === 'AbortError') return;

			console.error(error);
			const resultsContent = currentResults.querySelector('.blog-search-results-content');
			if ((resultsContent != null) && (originalContentHtml != null)) {
				resultsContent.innerHTML = originalContentHtml;
				setPaginatorPageIndexes(originalPaginatorPageIndexes);
				setPaginatorsDisabled(false);
				if (!paginatorInstances.length) {
					await mountPaginators().catch(console.error);
				}
			}
			else {
				currentResults.innerHTML =
					'<div class="blog-search-message blog-search-message-error" role="alert">' +
						'The search could not be loaded. Please submit the search again.' +
					'</div>';
			}
			currentResults.removeAttribute('aria-busy');
		}
		finally {
			if (paginationRequest === requestController) {
				paginationRequest = null;
				setSearchControlsDisabled(false);
			}
		}
	};

	forms.forEach((form) => {
		form.addEventListener('submit', (event) => {
			const input = form.querySelector('input[name="q"]');
			if (input == null) return;

			input.value = input.value.trim();
			input.setCustomValidity('');

			if (input.value.length < 2) {
				input.setCustomValidity('Enter at least 2 characters.');
			}
			else if (input.value.length > 50) {
				input.setCustomValidity('Keep your search to 50 characters or fewer.');
			}

			if (!form.checkValidity()) {
				event.preventDefault();
				input.reportValidity();
				return;
			}

			event.preventDefault();
			const targetUrl = new URL(form.action, window.location.origin);
			targetUrl.searchParams.set('q', input.value);
			targetUrl.searchParams.delete('page');
			targetUrl.searchParams.delete('results');

			window.sessionStorage.removeItem('blog-search-page:' + input.value);
			const currentPath = window.location.pathname.replace(/\/+$/, '');
			const targetPath = targetUrl.pathname.replace(/\/+$/, '');
			if ((currentPath === targetPath) && (document.getElementById('blog-search-current-results') != null)) {
				window.history.replaceState({}, '', targetUrl);
				loadPage(targetUrl, 0, true);
			}
			else {
				window.location.assign(targetUrl);
			}
		});
	});

	const visibleUrl = new URL(window.location.href);
	if (visibleUrl.searchParams.has('page')) {
		visibleUrl.searchParams.delete('page');
		window.history.replaceState({}, '', visibleUrl);
	}

	const currentResults = document.getElementById('blog-search-current-results');
	const initialSearchPending = currentResults?.querySelector('.blog-search-results-loading') != null;
	if (initialSearchPending) {
		const currentSearchTerm = visibleUrl.searchParams.get('q') ?? '';
		const savedPageIndex = window.sessionStorage.getItem('blog-search-page:' + currentSearchTerm);
		const initialPageIndex = Math.max(0, Number(savedPageIndex) || 0);
		loadPage(visibleUrl, initialPageIndex);
	}
	else {
		mountPaginators().catch(console.error);
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initPublicBlogSearch);
}
else {
	initPublicBlogSearch();
}
